import { useState } from 'react'
import arrowRight from '../../../../assets/icons/arrowRight.svg'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../redux'
import libertyLakeSlice from '../../../../redux/slice/libertyLake.slice'
import { LibertyLakeKid } from '../../../../types/libertyLakeV2'
import { formatNumberWithCommas } from '../../../../helpers/LibertyLake/helpers'
import { config as EnvironmentConfig } from '../../../../config'

const Step2 = () => {
  const dispatch: AppDispatch = useDispatch()
  const { kids, config } = useSelector((state: RootState) => state.libertyLake)

  const gradeOptions = ['Pre thru K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
  const weekOptions = Array.from({ length: 8 }, (_, i) => i + 2) // 2 to 9 weeks

  const [activeDropdown, setActiveDropdown] = useState<string>('')

  const updateKid = (kidId: string, updates: Partial<LibertyLakeKid>) => {
    const newKids = kids.map(k => k.id === kidId ? { ...k, ...updates } : k)
    dispatch(libertyLakeSlice.actions.setKids(newKids))
  }

  const getWeeklyRate = (kid: LibertyLakeKid): number => {
    if (!config) return 0
    const { basePricePerWeek, aMBusExtendedCare, pMBusExtendedCare } = config.pricing
    return basePricePerWeek + (kid.includeAMBus ? aMBusExtendedCare : 0) + (kid.includePMBus ? pMBusExtendedCare : 0)
  }

  const getMultiWeekDiscount = (weeksTotal: number): number => {
    if (!config || weeksTotal === 0) return 0
    return (config.discounts.multiWeek[weeksTotal.toString()] || 0) * weeksTotal
  }

  return <>
    <div className='step2'>
      {kids.map((kid) => (
        <div className='attendeeForm' key={kid.id}>
          <h3 className='attendeeFormTitle'>{kid.id}</h3>
          <div className="attendeeFormRow">
            <div className="inputFieldColumns">
              {/* Grade */}
              <div className="inputFieldArea">
                <label className='fieldLabel'>Select Grade (As of September, 2027)</label>
                <div
                  className="selectInputFieldArea"
                  onClick={() => setActiveDropdown(prev => prev === `grade-${kid.id}` ? '' : `grade-${kid.id}`)}
                >
                  {kid.grade.length ? kid.grade : 'Select...'}
                  <img className='arrowIcon' src={arrowRight} />
                </div>
                {activeDropdown === `grade-${kid.id}` && (
                  <div className="dropdownContainer">
                    {gradeOptions.map((grade, idx) => (
                      <div
                        className='dropdownOption'
                        key={`${grade}:${idx}`}
                        onClick={() => {
                          updateKid(kid.id, { grade })
                          setActiveDropdown('')
                        }}
                      >
                        {grade}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Week count */}
              <div className="inputFieldArea">
                <label className='fieldLabel'>Number of Weeks</label>
                <div
                  className="selectInputFieldArea"
                  onClick={() => setActiveDropdown(prev => prev === `week-${kid.id}` ? '' : `week-${kid.id}`)}
                >
                  {kid.weeksTotal > 0 ? `${kid.weeksTotal} Week${kid.weeksTotal > 1 ? 's' : ''}` : 'Select...'}
                  <img className='arrowIcon' src={arrowRight} />
                </div>
                {activeDropdown === `week-${kid.id}` && (
                  <div className="dropdownContainer">
                    {weekOptions.map((count) => (
                      <div
                        className='dropdownOption'
                        key={count}
                        onClick={() => {
                          updateKid(kid.id, { weeksTotal: count })
                          setActiveDropdown('')
                        }}
                      >
                        {count} Week{count > 1 ? 's' : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* AM / PM Bus + Extended Care add-ons */}
            {config && (config.pricing.aMBusExtendedCare > 0 || config.pricing.pMBusExtendedCare > 0) && (
              <div className="sessionWeeksColumn">
                <label className='fieldLabel' style={{ fontWeight: 'bold' }}>
                  Additional Options
                </label>
                <div className="sessionsWeeksContainer addOnsContainer">
                  {config.pricing.aMBusExtendedCare > 0 && (
                    <div className='sessionRow'>
                      <div className="sessionWeek addOnColumn">
                        <span className="addOnColLabel">AM</span>
                        <div className="addOnCheckRow">
                          <div className='checkboxArea'>
                            <input type='checkbox' checked={kid.includeAMBus} className='checkbox' onChange={() => {}} />
                            <label className='checkmark' onClick={() => updateKid(kid.id, { includeAMBus: !kid.includeAMBus })} />
                          </div>
                          <div
                            className="sessionColumn"
                            style={{ cursor: 'pointer' }}
                            onClick={() => updateKid(kid.id, { includeAMBus: !kid.includeAMBus })}
                          >
                            Bus / Extended Care
                            <span style={{ color: '#adaeb6', marginLeft: '5px', fontWeight: '600' }}>
                              (${config.pricing.aMBusExtendedCare} /week)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {config.pricing.pMBusExtendedCare > 0 && (
                    <div className='sessionRow'>
                      <div className="sessionWeek addOnColumn">
                        <span className="addOnColLabel">PM</span>
                        <div className="addOnCheckRow">
                          <div className='checkboxArea'>
                            <input type='checkbox' checked={kid.includePMBus} className='checkbox' onChange={() => {}} />
                            <label className='checkmark' onClick={() => updateKid(kid.id, { includePMBus: !kid.includePMBus })} />
                          </div>
                          <div
                            className="sessionColumn"
                            style={{ cursor: 'pointer' }}
                            onClick={() => updateKid(kid.id, { includePMBus: !kid.includePMBus })}
                          >
                            Bus / Extended Care
                            <span style={{ color: '#adaeb6', marginLeft: '5px', fontWeight: '600' }}>
                              (${config.pricing.pMBusExtendedCare} /week)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Weekly price preview */}
            {kid.weeksTotal > 0 && EnvironmentConfig.env === 'development' && (
              <div className="sessionWeeksColumn" style={{ marginTop: '10px' }}>
                <label className='fieldLabel'>
                  ${getWeeklyRate(kid)}/week × {kid.weeksTotal} week{kid.weeksTotal > 1 ? 's' : ''} = ${formatNumberWithCommas(getWeeklyRate(kid) * kid.weeksTotal)}
                  {getMultiWeekDiscount(kid.weeksTotal) > 0 && (
                    <> , Multi-week discount: -${formatNumberWithCommas(getMultiWeekDiscount(kid.weeksTotal))}</>
                  )}
                </label>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
    {activeDropdown && <div className='backDrop' onClick={() => setActiveDropdown('')} />}
  </>
}

export default Step2
